import { useEffect, useRef, useState } from 'react'
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon'
import { ensureBrandFont, ensureFontFaces } from '~/design-system/components/DeckGen/utils/brandFonts'
import { cardChrome, CARD_CHROME_TRANSITION } from '../birtvyChrome'
import styles from './BirtvyWebCard.module.css'
import { runWipe } from './webCardWipe'
import { arriveProps } from './birtvyArrive'
import { getBirtvyWebScroll, setBirtvyWebScroll } from '../web/birtvyWebScroll'
import {
  type BirtvyWeb,
  SOCIAL_BODY_MAX_HEIGHT,
  SOCIAL_BODY_MIN_HEIGHT,
  hostnameOf,
  proxiedImg,
  socialVideoSrc,
} from '../web/birtvyWeb'

// One Birtvy web card — the product's CanvasWebCards body (screenshot / social post / live iframe / brand
// tile / empty / error / loading), duplicated with the presentation store swapped for props and NUMBER ids
// (per "duplicate, don't mutate"). The parent (BirtvyCanvas) owns geometry, selection ring, drag, ports, the
// hover Extract toolbar + the external title pill; this file owns the card surface + its body dispatch.

const CARD_RADIUS = 20
const TILE_RADIUS = 12

/** The ready web card's scrollable screenshot — scroll position persisted (as a fraction) and shared with the
 *  fullscreen viewer via birtvyWebScroll, so the card and the open view stay at the same spot. */
function WebCardScreenshot({ web }: { web: BirtvyWeb }) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const save = () => {
      const max = el.scrollHeight - el.clientHeight
      setBirtvyWebScroll(web.id, max > 0 ? el.scrollTop / max : 0)
    }
    el.addEventListener('scroll', save, { passive: true })
    const fraction = getBirtvyWebScroll(web.id)
    let tries = 0
    const restore = () => {
      if (!scrollRef.current) return
      const m = scrollRef.current.scrollHeight - scrollRef.current.clientHeight
      if (m > 0) scrollRef.current.scrollTop = fraction * m
      else if (tries++ < 30) requestAnimationFrame(restore)
    }
    restore()
    return () => el.removeEventListener('scroll', save)
  }, [web.id])
  return (
    <div ref={scrollRef} className="h-full w-full overflow-hidden bg-white">
      <img src={web.screenshotUrl} alt={web.title || hostnameOf(web.url)} draggable={false} className="block w-full select-none" />
    </div>
  )
}

/** A chromeless, hover-to-play video inside a social post — a still at rest, looping silently on hover. */
function SocialVideo({ src, poster, width, height }: { src: string; poster?: string; width?: number; height?: number }) {
  const ref = useRef<HTMLVideoElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (el) el.muted = true
  }, [src])
  const aspectRatio = width && height ? `${width} / ${height}` : '16 / 9'
  return (
    <video
      ref={ref}
      src={socialVideoSrc(src)}
      poster={poster ? proxiedImg(poster) : undefined}
      muted
      loop
      playsInline
      preload="metadata"
      style={{ aspectRatio }}
      onMouseEnter={(event) => {
        const el = event.currentTarget
        el.muted = true
        void el.play().catch(() => {})
      }}
      onMouseLeave={(event) => {
        const el = event.currentTarget
        el.pause()
        el.currentTime = 0
      }}
      className="w-full cursor-pointer select-none rounded-xl bg-[var(--color-subtle)] object-cover"
    />
  )
}

/** A social post body (X / Instagram) — rendered when we have the post itself instead of a screenshot. Reads
 *  like an embedded post: author header (real avatar), text, then media. On the canvas the card auto-sizes to
 *  content (onHeightChange); the fullscreen viewer passes autoResize=false (it sizes + scrolls itself). */
export function BirtvyWebSocialBody({
  web,
  autoResize = true,
  onHeightChange,
}: {
  web: BirtvyWeb
  autoResize?: boolean
  onHeightChange?: (height: number) => void
}) {
  const post = web.post
  const contentRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!autoResize) return
    const el = contentRef.current
    if (!el) return
    const sync = () => {
      const next = Math.max(SOCIAL_BODY_MIN_HEIGHT, Math.min(SOCIAL_BODY_MAX_HEIGHT, Math.ceil(el.offsetHeight)))
      if (Math.abs(next - web.height) > 1) onHeightChange?.(next)
    }
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    return () => ro.disconnect()
  }, [autoResize, web.height, post, onHeightChange])

  if (!post) return null
  const author = (post.author ?? '').trim()
  return (
    <div className="h-full w-full overflow-hidden bg-white">
      <div ref={contentRef} className="flex flex-col gap-3 px-5 py-5">
        <div className="flex items-center gap-2.5">
          {post.authorAvatar ? (
            <img src={proxiedImg(post.authorAvatar)} alt="" draggable={false} className="h-9 w-9 shrink-0 select-none rounded-full object-cover" />
          ) : (
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-subtle)] text-sm font-semibold uppercase text-[var(--color-text-default)]">
              {(author || hostnameOf(web.url))[0]}
            </span>
          )}
          <div className="flex min-w-0 flex-col">
            {author ? <span className="truncate text-sm font-semibold leading-tight text-[var(--color-text-default)]">{author}</span> : null}
            <span className="truncate text-xs text-[var(--color-text-subtle)]">{hostnameOf(web.url)}</span>
          </div>
        </div>
        {post.text ? (
          <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-[var(--color-text-default)]">{post.text}</p>
        ) : null}
        {(post.videos ?? []).length > 0 ? (
          <div className="flex flex-col gap-2.5">
            {(post.videos ?? []).map((v) => (
              <SocialVideo key={v.url} src={v.url} poster={v.poster} width={v.width} height={v.height} />
            ))}
          </div>
        ) : null}
        {post.images.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            {post.images.map((src) => (
              <img key={src} src={proxiedImg(src)} alt="" draggable={false} className="w-full select-none rounded-xl object-cover" />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** A LIVE website embedded in the card (sites that allow framing). A transparent overlay keeps the card the
 *  drag handle / double-click-to-open target — the canvas card is a live thumbnail, not an interaction target. */
function WebCardLiveFrame({ web }: { web: BirtvyWeb }) {
  return (
    <div className="relative h-full w-full overflow-hidden bg-white">
      <iframe
        src={web.url}
        title={web.title || hostnameOf(web.url)}
        className="h-full w-full border-0"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        loading="lazy"
        tabIndex={-1}
        scrolling="no"
      />
      <div className="absolute inset-0" />
    </div>
  )
}

/** Empty state: a centred URL input that turns a blank (toolbar-created) card into a real one on submit. */
function EmptyWebCardBody({ web, onSubmitUrl }: { web: BirtvyWeb; onSubmitUrl?: (id: number, url: string) => void }) {
  const [value, setValue] = useState('')
  const ready = value.trim().length > 0
  const submit = () => {
    const url = value.trim()
    if (url) onSubmitUrl?.(web.id, url)
  }
  // ONE pill, not a field beside a button (Deck v0.8, Storyboard). The border belongs to the pill, so the
  // input inside it is bare — nesting the design system's Input here would draw a second box inside the
  // first. The submit button lives inside the pill and is the only thing that changes on input: grey while
  // there is nothing to send, primary the moment there is.
  return (
    <div className={`flex h-full w-full items-center justify-center bg-white px-8 ${styles.emptyBody}`}>
      {/* Only the CONTROL swallows the press, never the whole body: an empty card's body IS its drag
          handle, and stopping pointerdown across all of it meant a card you hadn't given a URL yet could
          not be selected, dragged or deleted at all. */}
      <div
        onPointerDown={(event) => event.stopPropagation()}
        className={`flex items-center border border-[var(--color-border-default)] ${styles.pill}`}
      >
        <DeckIcon name="global" size={20} className={`shrink-0 text-[var(--color-icon-default-muted)] ${styles.pillIcon}`} />
        <input
          autoFocus
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              submit()
            }
          }}
          placeholder="Paste a link"
          aria-label="Website URL"
          className={`min-w-0 flex-1 bg-transparent text-[var(--color-text-default)] outline-none placeholder:text-[var(--color-text-hint)] ${styles.pillInput}`}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!ready}
          aria-label="Add website"
          className={`flex shrink-0 items-center justify-center rounded-full transition-colors duration-150 ${styles.pillSubmit} ${
            ready
              ? 'bg-[var(--color-state-primary)] text-white'
              : 'bg-[var(--color-state-disabled)] text-[var(--color-icon-default-muted)]'
          }`}
        >
          <DeckIcon name="arrow-up" size={20} />
        </button>
      </div>
    </div>
  )
}

// ── Brand-style tile bodies (swatch / fonts / logo) — ported verbatim from CanvasWebCards.tsx ───────────────

function SwatchBody({ web }: { web: BirtvyWeb }) {
  const sw = web.swatch
  if (!sw) return null
  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className="relative min-h-0 flex-1" style={{ backgroundColor: sw.color }}>
        <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/[0.06]" />
      </div>
      <div className="flex shrink-0 flex-col items-center gap-0.5 border-t border-[var(--color-border-default)] px-1.5 py-1.5">
        <span className="font-mono text-[10px] leading-none text-[var(--color-text-default)]">{sw.color}</span>
        {sw.label ? (
          <span className="max-w-full truncate text-[8px] font-semibold uppercase leading-none tracking-wide text-[var(--color-text-subtle)]">{sw.label}</span>
        ) : null}
      </div>
    </div>
  )
}

function FontRow({ label, family }: { label: string; family: string }) {
  const stack = `"${family}", ui-sans-serif, system-ui, sans-serif`
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-subtle)] text-xl leading-none text-[var(--color-text-default)]" style={{ fontFamily: stack }}>
        Ag
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[8px] font-semibold uppercase leading-none tracking-wide text-[var(--color-text-subtle)]">{label}</span>
        <span className="truncate text-[13px] font-medium leading-tight text-[var(--color-text-default)]" style={{ fontFamily: stack }}>{family}</span>
      </div>
    </div>
  )
}

function FontsBody({ web }: { web: BirtvyWeb }) {
  const heading = web.fonts?.heading
  const body = web.fonts?.body
  return (
    <div className="flex h-full w-full flex-col justify-center gap-2 bg-white px-3.5 py-3">
      {heading ? <FontRow label="Heading" family={heading} /> : null}
      {heading && body ? <div className="h-px w-full bg-[var(--color-border-default)]" /> : null}
      {body ? <FontRow label="Body" family={body} /> : null}
    </div>
  )
}

function LogoBody({ web }: { web: BirtvyWeb }) {
  const src = web.logo?.src
  if (!src) return null
  return (
    <div className="group/lg relative flex h-full w-full items-center justify-center bg-[var(--color-subtle)] p-3">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            'linear-gradient(45deg, rgba(9,9,11,0.04) 25%, transparent 25%), linear-gradient(-45deg, rgba(9,9,11,0.04) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(9,9,11,0.04) 75%), linear-gradient(-45deg, transparent 75%, rgba(9,9,11,0.04) 75%)',
          backgroundSize: '14px 14px',
          backgroundPosition: '0 0, 0 7px, 7px -7px, -7px 0',
        }}
      />
      <img src={src} alt="Brand logo" draggable={false} className="relative max-h-full max-w-full select-none object-contain" />
      <span className="absolute left-1.5 top-1.5 rounded-[5px] bg-white/70 px-1.5 py-0.5 text-[8px] font-semibold uppercase leading-none tracking-wide text-[var(--color-text-subtle)] opacity-0 backdrop-blur-sm transition-opacity duration-150 group-hover/lg:opacity-100">
        Logo
      </span>
    </div>
  )
}

/** The card body dispatch — empty input, social post, live iframe, screenshot, error, or loading caret. */
function CardBody({ web, onSubmitUrl, onHeightChange }: { web: BirtvyWeb; onSubmitUrl?: (id: number, url: string) => void; onHeightChange?: (h: number) => void }) {
  if (web.status === 'empty') return <EmptyWebCardBody web={web} onSubmitUrl={onSubmitUrl} />
  if (web.post) return <BirtvyWebSocialBody web={web} onHeightChange={onHeightChange} />
  if (web.status === 'ready' && web.frameable) return <WebCardLiveFrame web={web} />
  if (web.status === 'ready' && web.screenshotUrl) return <WebCardScreenshot web={web} />
  if (web.status === 'error') {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-white px-6 text-center">
        <span className="text-sm font-medium text-[var(--color-text-default)]">Couldn’t load this site</span>
        <span className="max-w-full truncate text-xs text-[var(--color-text-subtle)]">{web.url}</span>
      </div>
    )
  }
  // The sweep covering this is rendered by the CARD, not here — it has to outlive the swap from loading to
  // ready, and a body that unmounts on that swap cannot. What this draws is the surface the sweep carries
  // off: a page that has not arrived yet, not an empty rectangle.
  return (
    <div className={styles.skeleton}>
      <div className={styles.skelNav}>
        <div className={styles.skelMark} />
        <div className={styles.skelLink} />
        <div className={styles.skelLink} />
        <div className={styles.skelLink} />
        <div className={styles.skelPill} />
      </div>
      <div className={styles.skelBody}>
        <div className={styles.skelCopy}>
          <div className={styles.skelHead} />
          <div className={styles.skelHead} style={{ width: '82%' }} />
          <div className={styles.skelLine} style={{ width: '64%' }} />
          <div className={styles.skelLine} style={{ width: '48%' }} />
        </div>
        <div className={styles.skelBlock} />
      </div>
    </div>
  )
}

/** One Birtvy web card. The parent owns geometry (web.x/y/w/h), selection ring, drag (armed via
 *  onPointerDown), ports + the hover Extract toolbar; this component owns the card surface + its body. */
export function BirtvyWebCard({
  web,
  z,
  selected,
  onPointerDown,
  onPointerEnter,
  onPointerLeave,
  onOpen,
  onSubmitUrl,
  onHeightChange,
  animateIn = false,
}: {
  web: BirtvyWeb
  z?: number
  selected: boolean
  onPointerDown: (e: React.PointerEvent) => void
  onPointerEnter: () => void
  onPointerLeave: () => void
  /** Double-click a ready card → open the fullscreen viewer. */
  onOpen: () => void
  onSubmitUrl?: (id: number, url: string) => void
  onHeightChange?: (h: number) => void
  /**
   * Play the arrival on mount. OFF by default, and deliberately: every card on the board mounts when a deck
   * loads, so turning this on globally would pop fifty cards at once on open. It is for cards that actually
   * arrive — today, a scripted demo's.
   */
  animateIn?: boolean
}) {
  const isTile = web.kind === 'swatch' || web.kind === 'fonts' || web.kind === 'logo'
  const ready = web.status === 'ready' || !!web.post

  /**
   * The loading→ready moment, which is the only thing that starts the sweep travelling.
   *
   * Tracked here rather than derived from `status` because "is ready" and "just became ready" are different
   * questions: a card hydrated straight into `ready` from a saved deck must NOT sweep — nothing arrived,
   * it was already there. Only a card that was watched loading gets the reveal.
   */
  const prevStatus = useRef(web.status)
  const [revealing, setRevealing] = useState(false)
  const wipeRef = useRef<HTMLCanvasElement | null>(null)
  useEffect(() => {
    if (prevStatus.current === 'loading' && web.status === 'ready') setRevealing(true)
    prevStatus.current = web.status
  }, [web.status])

  // The sweep runs once and then the canvas comes off. `onFinish` also fires when WebGPU is unavailable, so
  // the unsupported path is the same path — the page is simply already revealed.
  useEffect(() => {
    if (!revealing) return
    const canvas = wipeRef.current
    if (!canvas) {
      setRevealing(false)
      return
    }
    return runWipe(canvas, {}, () => setRevealing(false))
  }, [revealing])

  // Fonts tiles render in the real typefaces; cards with an extracted style load the site's @font-face files.
  useEffect(() => {
    if (web.kind === 'fonts' && web.fonts) {
      ensureBrandFont(web.fonts.heading)
      ensureBrandFont(web.fonts.body)
    }
    if (web.brandStyle?.fontFaces?.length) ensureFontFaces(web.brandStyle.fontFaces)
  }, [web.kind, web.fonts, web.brandStyle])

  const arrive = isTile ? arriveProps(web, 0) : null
  return (
    <div
      data-web-id={web.id}
      className={animateIn ? (isTile ? arrive?.className : styles.enter) : undefined}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onDoubleClick={(e) => {
        e.stopPropagation()
        if (ready && !isTile) onOpen()
      }}
      style={{
        ...(animateIn && isTile ? arrive?.style : null),
        position: 'absolute',
        left: web.x,
        top: web.y,
        width: web.width,
        height: web.height,
        borderRadius: isTile ? TILE_RADIUS : CARD_RADIUS,
        background: '#ffffff',
        zIndex: z,
        overflow: 'hidden',
        cursor: ready && !isTile ? 'pointer' : 'grab',
        // Shared card chrome (birtvyChrome) — identical selection ring + rest border as every other element.
        boxShadow: cardChrome(selected),
        transition: CARD_CHROME_TRANSITION,
      }}
    >
      {isTile ? (
        web.kind === 'swatch' ? <SwatchBody web={web} /> : web.kind === 'logo' ? <LogoBody web={web} /> : <FontsBody web={web} />
      ) : (
        <CardBody web={web} onSubmitUrl={onSubmitUrl} onHeightChange={onHeightChange} />
      )}
      {revealing ? <canvas ref={wipeRef} aria-hidden className={styles.wipeCanvas} /> : null}
    </div>
  )
}
